/**
 * Landing-page links into Create Account.
 *
 * A partner link such as
 *   https://site.labsmarts.com/?signup&ref=mvx&utm_source=darren&utm_campaign=mvx
 * opens straight on Create Account (see SecureApp.tsx) and pre-fills
 * "How Did You Hear About Us?" from `ref` (see LabSmartsSignUp).
 *
 * The query string is never rewritten. The ref and utm_* values are also kept
 * in localStorage so they outlive a refresh, and so the pre-fill comes back
 * after a trip to Sign In: Amplify clears every typed sign-up value whenever
 * the screen is hidden, so the screen re-reads this each time it shows.
 */

export const ATTRIBUTION_KEY = 'ls_attribution';

export const REFERRAL_SOURCE_KEY = 'custom:referral_source';

// What a known `ref` puts in "How Did You Hear About Us?". An unknown ref
// leaves the field blank rather than echoing a raw code at the practitioner.
export const REFERRAL_SOURCES: { [ref: string]: string } = {
  mvx: 'Dr. Darren Schmidt / MVX',
  'thyroid-guide': 'LabSmarts Thyroid Guide',
};

export type Attribution = { [param: string]: string };

/** Presence only: `?signup`, `?signup=1` and `?signup=true` all count. */
export function opensOnSignUp(search: string = window.location.search): boolean {
  return new URLSearchParams(search).has('signup');
}

/** `ref` plus every `utm_*` param on the URL, skipping blanks. */
export function readAttribution(search: string): Attribution {
  const attribution: Attribution = {};
  new URLSearchParams(search).forEach((value, key) => {
    if ((key === 'ref' || key.startsWith('utm_')) && value.trim()) {
      attribution[key] = value.trim();
    }
  });
  return attribution;
}

export function storedAttribution(): Attribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    return null;
  }
}

/**
 * First touch wins: once a visit has recorded attribution, later links do not
 * replace it. A URL with no ref or utm_* params writes nothing.
 */
export function captureAttribution(search: string = window.location.search): void {
  const attribution = readAttribution(search);
  if (Object.keys(attribution).length === 0 || storedAttribution()) {
    return;
  }
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch (err) {
    // Blocked storage only costs the pre-fill after a refresh; never block sign-up.
  }
}

/** The "How Did You Hear About Us?" pre-fill, or '' for an unknown or missing ref. */
export function referralSourcePrefill(): string {
  const stored = storedAttribution() || {};
  const ref = (stored.ref || readAttribution(window.location.search).ref || '').toLowerCase();
  return REFERRAL_SOURCES[ref] || '';
}
